import React, { useEffect, useState } from 'react';
import { Box, Progress, SimpleGrid, Stat, StatLabel, StatNumber, Text } from '@chakra-ui/react';
import { api } from '../../services/api';
import { DataSkeleton, ErrorState } from '../states';

export interface NotificationMetricsData {
  recipient_count: number;
  reads: number;
  unread: number;
  read_percentage: number;
}

export function NotificationMetricsView({ metrics }: { metrics: NotificationMetricsData }) {
  return (
    <Box background="white" borderRadius="md" boxShadow="sm" p={5} mb={6} data-testid="metrics">
      <Text fontSize="lg" fontWeight="bold" mb={4}>
        Métricas de leitura
      </Text>
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={5}>
        <Stat>
          <StatLabel>Destinatários</StatLabel>
          <StatNumber>{metrics.recipient_count}</StatNumber>
        </Stat>
        <Stat>
          <StatLabel>Leituras</StatLabel>
          <StatNumber>{metrics.reads}</StatNumber>
        </Stat>
        <Stat>
          <StatLabel>Não lidas</StatLabel>
          <StatNumber>{metrics.unread}</StatNumber>
        </Stat>
        <Stat>
          <StatLabel>Percentual lido</StatLabel>
          <StatNumber>{metrics.read_percentage.toFixed(2)}%</StatNumber>
        </Stat>
      </SimpleGrid>
      <Progress
        mt={4}
        value={metrics.read_percentage}
        colorScheme="orange"
        borderRadius="full"
        aria-label={`${metrics.read_percentage.toFixed(2)}% das notificações foram lidas`}
      />
    </Box>
  );
}

export function NotificationMetrics({ notificationId }: { notificationId: number }) {
  const [metrics, setMetrics] = useState<NotificationMetricsData | null>(null);
  const [error, setError] = useState('');
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let active = true;
    api
      .get(`/v1/admin/notifications/${notificationId}/metrics`)
      .then((response) => {
        if (active) {
          setMetrics(response.data?.data);
        }
      })
      .catch((requestError) => {
        if (active) {
          setError(requestError.response?.data?.msg || 'Não foi possível carregar as métricas');
        }
      });
    return () => {
      active = false;
    };
  }, [notificationId, retryCount]);

  if (error) {
    return (
      <Box mb={6}>
        <ErrorState
          title="Métricas indisponíveis"
          description={error}
          onRetry={() => {
            setError('');
            setRetryCount((current) => current + 1);
          }}
        />
      </Box>
    );
  }
  if (!metrics) {
    return <DataSkeleton variant="cards" rows={4} label="Carregando métricas" />;
  }
  return <NotificationMetricsView metrics={metrics} />;
}
